import { Show } from 'solid-js'
import { Portal } from 'solid-js/web'
import { userStore } from '/web/store'
import { hasActivePremium } from '/common/subscription-access'

/**
 * #216 — the mobile "Free Tokens" bar, hoisted out of the home hero (HomeSpotlight) so
 * it floats just above the bottom nav on EVERY AI Fans page, not only home. Portaled to
 * <body> + position:fixed so it never scrolls away and escapes any ancestor
 * overflow/containing-block clip. Mobile-only: it tracks the bottom tab bar, which is
 * hidden at ≥640px.
 *
 * #216 (follow-up, per Andy): the bar is REMOVED entirely on every page once the user
 * has accepted the free tokens (`hasClaimedInitialTokens`) OR holds an active premium
 * package (`hasActivePremium`). Before that it invites them to claim; the previous
 * "Tokens Ready / CLAIMED" post-claim variant is gone by design.
 *
 * The markup + animations are copied verbatim from the original home-hero banner (#192,
 * #197) so the look is identical — Andy already approved that treatment on home.
 */
export function MobileFreeTokensBar() {
  const state = () => userStore()
  const loggedIn = () => state().loggedIn
  const claimed = () => !!state().profile?.hasClaimedInitialTokens
  const hasPremium = () => hasActivePremium(state().aifansSubscriptions)
  // For a logged-in user the claim state lives on the profile, which hydrates a beat
  // after auth. Until it's loaded we can't know if they've claimed, so hold the bar back
  // to avoid a flash on claimed/premium accounts right after a reload. Logged-out users
  // are always unclaimed, so show immediately.
  const claimStateReady = () => !loggedIn() || !!state().profile
  // Hide on all pages once the free tokens are claimed OR a package is owned.
  const shouldShow = () => claimStateReady() && !claimed() && !hasPremium()

  return (
    <Show when={shouldShow()}>
      <Portal>
        <div
          class="cf-ft-global-bar cf-ft-sheen-card fixed left-3 right-3 z-[99] flex cursor-pointer items-center gap-3 rounded-[14px] border border-[rgba(138,43,226,0.55)]"
          style={{
            bottom: 'calc(var(--cf-safe-bot) + 8px + var(--cf-tabbar-h) + 12px)',
            'backdrop-filter': 'blur(20px)',
            '-webkit-backdrop-filter': 'blur(20px)',
            animation: 'cfFtCardGlow 3.4s ease-in-out infinite',
            padding: '12px 14px',
          }}
          onClick={() => {
            if (loggedIn()) userStore.buyTokensModal(true)
            else userStore.loginModal(true)
          }}
        >
          {/* Icon — gift; decorative */}
          <span
            class="flex-shrink-0"
            aria-hidden="true"
            style={{
              'font-size': '28px',
              'line-height': '1',
              filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.6))',
            }}
          >
            🎁
          </span>

          <div class="min-w-0 flex-1 text-left">
            {/* Eyebrow */}
            <div
              class="font-rajdhani font-extrabold uppercase text-[#FF3CAC]"
              style={{ 'font-size': '10px', 'letter-spacing': '1.8px' }}
            >
              FREE BONUS
            </div>
            {/* Headline with shimmer */}
            <div class="font-bebas leading-none" style={{ 'font-size': '22px', 'letter-spacing': '1px' }}>
              <span class="ftc-headline">FREE TOKENS</span>
            </div>
            {/* Sub */}
            <div class="whitespace-nowrap font-nunito" style={{ 'font-size': '12px', color: '#B0B0B0' }}>
              Unlock instantly on signup
            </div>
          </div>

          {/* Value badge */}
          <div
            class="cf-ft-badge flex-shrink-0 whitespace-nowrap font-rajdhani font-extrabold uppercase text-white"
            style={{
              background: '#FF3CAC',
              'border-radius': '5px',
              padding: '4px 8px',
              'font-size': '10px',
              'letter-spacing': '1px',
              'box-shadow': '0 4px 16px rgba(255,60,172,0.5)',
              animation: 'cfFtBadgePulse 2s ease-in-out infinite',
            }}
          >
            $19.99 VALUE
          </div>
        </div>
      </Portal>

      <style>{`
        /* #216 — mobile-only visibility, tracks the bottom tab bar (hidden ≥640px). */
        .cf-ft-global-bar { display: flex; }
        @media (min-width: 640px) { .cf-ft-global-bar { display: none !important; } }

        @keyframes cfFtCardGlow {
          0%, 100% {
            box-shadow: 0 14px 38px rgba(0,0,0,0.5), 0 0 30px rgba(255,60,172,0.35), 0 0 60px rgba(138,43,226,0.2);
            border-color: rgba(138,43,226,0.55);
          }
          50% {
            box-shadow: 0 14px 38px rgba(0,0,0,0.5), 0 0 40px rgba(255,60,172,0.52), 0 0 70px rgba(138,43,226,0.3);
            border-color: rgba(255,60,172,0.8);
          }
        }
        @keyframes ftShimmer { to { background-position: -200% center; } }
        @keyframes ftSheen { 0% { left: -65%; } 45%, 100% { left: 135%; } }
        @keyframes cfFtBadgePulse {
          0%, 100% { transform: scale(1);    box-shadow: 0 4px 16px rgba(255,60,172,0.45); }
          50%      { transform: scale(1.05); box-shadow: 0 6px 22px rgba(255,60,172,0.72); }
        }
        .cf-ft-sheen-card {
          overflow: hidden;
          background:
            radial-gradient(150% 130% at 0% 0%, rgba(138,43,226,0.34), transparent 55%),
            radial-gradient(150% 130% at 100% 100%, rgba(0,229,255,0.22), transparent 55%),
            rgba(11,11,15,0.92) !important;
          box-shadow: 0 14px 38px rgba(0,0,0,0.5), 0 0 30px rgba(255,60,172,0.35), 0 0 60px rgba(138,43,226,0.2);
        }
        .cf-ft-sheen-card::after {
          content: "";
          position: absolute; top: 0; left: -65%;
          width: 55%; height: 100%;
          background: linear-gradient(100deg, transparent, rgba(255,255,255,0.16), transparent);
          transform: skewX(-18deg);
          animation: ftSheen 6s ease-in-out infinite;
          pointer-events: none;
        }
        .ftc-headline {
          font-family: 'Bebas Neue', sans-serif;
          background: linear-gradient(90deg, #8A2BE2, #FF3CAC, #00E5FF, #8A2BE2);
          background-size: 200% auto;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          color: transparent;
          animation: ftShimmer 3s linear infinite;
          display: inline-block;
        }
        @media (prefers-reduced-motion: reduce) {
          .cf-ft-sheen-card { animation: none !important; }
          .cf-ft-sheen-card::after { animation: none !important; }
          .ftc-headline { animation: none !important; background-position: 0 center; }
          .cf-ft-badge { animation: none !important; transform: none !important; }
        }
      `}</style>
    </Show>
  )
}
